import React, { useEffect, useState, useCallback, useRef } from 'react';
import heic2any from 'heic2any';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Divider, Grid, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Alert,
  useMediaQuery, useTheme,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SellIcon from '@mui/icons-material/AttachMoney';
import api from '../api';

const STATUS_COLORS = { active: 'success', sold: 'warning', deceased: 'error' };

const InfoRow = ({ label, value }) =>
  value != null && value !== '' ? (
    <Box>
      <Typography variant="caption" color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
        {label}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </Box>
  ) : null;

function AddWormingDialog({ cowId, open, onClose, onSaved }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState({ date: '', product: '', notes: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/worming/', { ...form, cow: cowId });
      onSaved();
      onClose();
      setForm({ date: '', product: '', notes: '' });
    } catch {
      setError('Failed to save worming record.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>Add Worming Record</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box component="form" id="worming-form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField label="Date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required fullWidth InputLabelProps={{ shrink: true }} />
          <TextField label="Product" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} required fullWidth />
          <TextField label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} fullWidth multiline rows={2} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" form="worming-form" variant="contained" disabled={saving}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

function CowPhoto({ src, alt }) {
  const [displaySrc, setDisplaySrc] = useState(null);
  const objectUrlRef = useRef(null);

  useEffect(() => {
    const isHeic = /\.(heic|heif)$/i.test(src);
    if (isHeic) {
      fetch(src)
        .then((r) => r.blob())
        .then((blob) => heic2any({ blob, toType: 'image/jpeg', quality: 0.85 }))
        .then((converted) => {
          const url = URL.createObjectURL(converted);
          objectUrlRef.current = url;
          setDisplaySrc(url);
        })
        .catch(() => setDisplaySrc(src));
    } else {
      setDisplaySrc(src);
    }
    return () => { if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current); };
  }, [src]);

  if (!displaySrc) return <Box sx={{ width: 160, height: 120, bgcolor: 'grey.100', borderRadius: 1 }} />;
  return (
    <Box
      component="img"
      src={displaySrc}
      alt={alt}
      sx={{ height: 160, maxWidth: 220, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
    />
  );
}

function SellCowDialog({ cow, open, onClose, onSold }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState({ sold_date: '', sold_amount: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) setForm({ sold_date: '', sold_amount: '' });
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.patch(`/cows/${cow.id}/`, {
        sold_date: form.sold_date,
        sold_amount: form.sold_amount,
      });
      await api.post('/income/', {
        farmer: cow.owner,
        date: form.sold_date,
        amount: form.sold_amount,
        description: `Sale of cow ${cow.cow_number}`,
        source_type: 'cow_sale',
        cow: cow.id,
      });
      await onSold();
      onClose();
    } catch (err) {
      const data = err.response?.data;
      setError(data ? Object.entries(data).map(([k, v]) => `${k}: ${v}`).join(' | ') : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth fullScreen={isMobile}>
      <DialogTitle>Sell Cow {cow?.cow_number}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box component="form" id="sell-form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField
            label="Sale Date"
            type="date"
            value={form.sold_date}
            onChange={(e) => setForm({ ...form, sold_date: e.target.value })}
            required
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Sale Amount ($)"
            type="number"
            value={form.sold_amount}
            onChange={(e) => setForm({ ...form, sold_amount: e.target.value })}
            required
            fullWidth
            inputProps={{ step: '0.01', min: '0' }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type="submit"
          form="sell-form"
          variant="contained"
          color="success"
          disabled={saving || !form.sold_date || !form.sold_amount}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SellIcon />}
        >
          {saving ? 'Saving…' : 'Record Sale'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function CowDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cow, setCow] = useState(null);
  const [worming, setWorming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wormingDialog, setWormingDialog] = useState(false);
  const [sellDialog, setSellDialog] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  const handleAddPhoto = async (e) => {
    let file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    const isHeic = /\.(heic|heif)$/i.test(file.name) || file.type === 'image/heic' || file.type === 'image/heif';
    if (isHeic) {
      try {
        const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
        file = new File([blob], file.name.replace(/\.heic$/i, '.jpg'), { type: 'image/jpeg' });
      } catch {
        setUploadingPhoto(false);
        return;
      }
    }
    try {
      const fd = new FormData();
      fd.append('cow', id);
      fd.append('image', file);
      fd.append('is_main', String(!cow.cow_images?.length));
      await api.post('/cow-images/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await fetchCow();
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const fetchCow = useCallback(async () => {
    const res = await api.get(`/cows/${id}/`);
    setCow(res.data);
  }, [id]);

  const fetchWorming = useCallback(async () => {
    const res = await api.get('/worming/', { params: { cow: id } });
    setWorming(res.data);
  }, [id]);

  useEffect(() => {
    Promise.all([fetchCow(), fetchWorming()]).finally(() => setLoading(false));
  }, [fetchCow, fetchWorming]);

  if (loading) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  }
  if (!cow) {
    return <Box p={4}><Typography>Cow not found.</Typography></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000, mx: 'auto' }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <IconButton onClick={() => navigate('/herd')} aria-label="Back to herd"><ArrowBackIcon /></IconButton>
        <Typography variant="h4" fontWeight={700} sx={{ flexGrow: 1 }}>
          {cow.cow_number}
        </Typography>
        <Chip label={cow.status} color={STATUS_COLORS[cow.status] ?? 'default'} sx={{ mr: 1 }} />
        {cow.status === 'active' && (
          <Button variant="outlined" color="success" startIcon={<SellIcon />} onClick={() => setSellDialog(true)} sx={{ mr: 1 }}>
            Sell
          </Button>
        )}
        <Button variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/herd/${id}/edit`)}>
          Edit
        </Button>
      </Box>

      <Grid container spacing={3}>
          {/* Identity */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>Identity</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4}><InfoRow label="Owner" value={cow.owner} /></Grid>
              <Grid item xs={6} sm={4}><InfoRow label="Sex" value={cow.sex} /></Grid>
              <Grid item xs={6} sm={4}><InfoRow label="Breed" value={cow.breed} /></Grid>
              <Grid item xs={6} sm={4}><InfoRow label="Date of Birth" value={cow.dob} /></Grid>
              <Grid item xs={6} sm={4}><InfoRow label="Age" value={cow.age != null ? `${cow.age} yr` : null} /></Grid>
              <Grid item xs={6} sm={4}>
                <InfoRow label="Expense Share" value={cow.cow_share != null ? Number(cow.cow_share).toFixed(2) : null} />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Acquisition */}
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>Acquisition</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}><InfoRow label="Bought Date" value={cow.bought_date} /></Grid>
              <Grid item xs={6}><InfoRow label="Purchase Price" value={cow.purchase_price ? `$${cow.purchase_price}` : null} /></Grid>
              <Grid item xs={6}><InfoRow label="Weaned Date" value={cow.weaned_date} /></Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Disposition */}
        {(cow.sold_date || cow.death_date) && (
          <Grid item xs={12} sm={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                {cow.death_date ? 'Deceased' : 'Sold'}
              </Typography>
              <Grid container spacing={2}>
                {cow.sold_date && <Grid item xs={6}><InfoRow label="Sold Date" value={cow.sold_date} /></Grid>}
                {cow.sold_amount && <Grid item xs={6}><InfoRow label="Sold Amount" value={`$${cow.sold_amount}`} /></Grid>}
                {cow.death_date && <Grid item xs={6}><InfoRow label="Death Date" value={cow.death_date} /></Grid>}
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Genealogy */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>Genealogy</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <InfoRow label="Dam (Mother)" value={cow.dam ?? '—'} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <InfoRow label="Sire (Father)" value={cow.sire ?? '—'} />
              </Grid>
            </Grid>
            {cow.calves_as_dam?.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary" textTransform="uppercase">Calves</Typography>
                <TableContainer sx={{ mt: 1, overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell scope="col">Tag</TableCell>
                        <TableCell scope="col">DOB</TableCell>
                        <TableCell scope="col">Sex</TableCell>
                        <TableCell scope="col">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cow.calves_as_dam.map((calf) => (
                        <TableRow
                          key={calf.id}
                          hover
                          tabIndex={0}
                          sx={{
                            cursor: 'pointer',
                            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
                          }}
                          onClick={() => navigate(`/herd/${calf.id}`)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/herd/${calf.id}`); }
                          }}
                          aria-label={`View calf ${calf.cow_number}`}
                        >
                          <TableCell sx={{ fontWeight: 600 }}>{calf.cow_number}</TableCell>
                          <TableCell>{calf.dob}</TableCell>
                          <TableCell>{calf.sex}</TableCell>
                          <TableCell>
                            <Chip size="small" label={calf.status} color={STATUS_COLORS[calf.status] ?? 'default'} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Paper>
        </Grid>

        {/* Worming History */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2" color="text.secondary">Worming History</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={() => setWormingDialog(true)}>
                Add
              </Button>
            </Box>
            {worming.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No worming records.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell scope="col">Date</TableCell>
                      <TableCell scope="col">Product</TableCell>
                      <TableCell scope="col">Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {worming.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>{r.date}</TableCell>
                        <TableCell>{r.product}</TableCell>
                        <TableCell>{r.notes}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Photos */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2" color="text.secondary">Photos</Typography>
              <Box>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif"
                  style={{ display: 'none' }}
                  ref={photoInputRef}
                  onChange={handleAddPhoto}
                />
                <Button
                  size="small"
                  startIcon={uploadingPhoto ? <CircularProgress size={14} /> : <AddPhotoAlternateIcon />}
                  onClick={() => photoInputRef.current.click()}
                  disabled={uploadingPhoto}
                >
                  {uploadingPhoto ? 'Uploading…' : 'Add Photo'}
                </Button>
              </Box>
            </Box>
            {cow.cow_images?.length > 0 ? (
              <Box display="flex" gap={1.5} flexWrap="wrap">
                {cow.cow_images.map((img) => (
                  <CowPhoto key={img.id} src={img.image} alt={cow.cow_number} />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">No photos yet.</Typography>
            )}
          </Paper>
        </Grid>

        {/* Notes */}
        {cow.notes && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>Notes</Typography>
              <Typography variant="body2">{cow.notes}</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      <AddWormingDialog
        cowId={id}
        open={wormingDialog}
        onClose={() => setWormingDialog(false)}
        onSaved={fetchWorming}
      />

      <SellCowDialog
        cow={cow}
        open={sellDialog}
        onClose={() => setSellDialog(false)}
        onSold={fetchCow}
      />
    </Box>
  );
}
